import {Component, viewChild, signal, inject, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {EmailService} from '../../../../services/http/email.service';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * All the data the email form modal needs.
 */
export interface EmailFormModalData {
  base64EncodedPdf?: string;
  pdfName?: string;
}

/**
 * This component handles sending an email.
 */
@Component({
  selector: 'app-email-form',
  templateUrl: './email-form.component.html',
  styleUrls: ['./email-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class EmailFormComponent {
  private toastr = inject(ToastrService);
  private emailService = inject(EmailService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** A modal component. */
  modalComponent = viewChild<ModalComponent>('modalComponent');
  /** A PDF data, which was encoded in base-64. */
  readonly base64EncodedPdf = signal<string>(undefined);
  /** The name of a PDF. */
  readonly pdfName = signal<string>(undefined);

  /** `True` if request sending to backend is in progress. */
  loading = signal(false);
  /** A comma separated string of emails addresses, who should receive an email. */
  recipientsString = signal('');

  static open(modalService: NgbModal, data: EmailFormModalData): NgbModalRef {
    const modalRef = modalService.open(EmailFormComponent);
    const instance = modalRef.componentInstance as EmailFormComponent;
    if (data.base64EncodedPdf !== undefined) {
      instance.base64EncodedPdf.set(data.base64EncodedPdf);
    }
    if (data.pdfName !== undefined) {
      instance.pdfName.set(data.pdfName);
    }
    return modalRef;
  }

  /**
   * Sends request to backend with email data to be sent.
   */
  onSubmit(): void {
    this.loading.set(true);
    const recipients = this.recipientsString().split(',');

    this.emailService.sendEmail(this.base64EncodedPdf(), this.pdfName(), recipients)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.modalComponent().activeModal.close();
                this.loading.set(false);
                this.toastr.success('Email has been successfully sent');
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}